import type { StalenessLevel } from "../types/brand";

const MS_PER_DAY = 86_400_000;
const STALE_THRESHOLD_DAYS = 60;
const CRITICAL_THRESHOLD_DAYS = 90;

interface BrandStaleness {
  ageInDays: number | null;
  level: StalenessLevel;
  color: "gray" | "green" | "yellow" | "red";
  label: string;
}

function formatRelativeTime(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 month ago";
  return `${months} months ago`;
}

/**
 * Compute staleness level from a last-refresh timestamp.
 * Pure function — no hooks, safe to call from render or effects.
 */
export function getBrandStaleness(
  lastRefreshAt: string | null,
  now: number = Date.now(),
): BrandStaleness {
  if (lastRefreshAt === null) {
    return { ageInDays: null, level: "never", color: "gray", label: "Never refreshed" };
  }

  const ageMs = now - new Date(lastRefreshAt).getTime();
  const ageInDays = Math.floor(ageMs / MS_PER_DAY);

  if (ageInDays >= CRITICAL_THRESHOLD_DAYS) {
    return { ageInDays, level: "critical", color: "red", label: `Refreshed ${formatRelativeTime(ageInDays)}` };
  }
  if (ageInDays >= STALE_THRESHOLD_DAYS) {
    return { ageInDays, level: "stale", color: "yellow", label: `Refreshed ${formatRelativeTime(ageInDays)}` };
  }
  return { ageInDays, level: "fresh", color: "green", label: `Refreshed ${formatRelativeTime(ageInDays)}` };
}
